import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  alpha,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBackRounded,
  BatteryFullRounded,
  SignalCellularAltRounded,
  ThermostatRounded,
  TimerRounded,
  MyLocationRounded,
  LanRounded,
  SensorsRounded,
  RefreshRounded,
  PowerSettingsNewRounded,
  ViewCarouselRounded,
  BoltRounded,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api, { errorMessage } from '../lib/api';
import { formatDateTime, relativeTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, StatusPill, EmptyState, PanelLoader } from '../components/ui/GlassPanel';
import { HoloLineChart } from '../components/charts/HoloChart';
import DeviceModel3D from '../components/three/DeviceModel3D';
import { useToast } from '../components/ui/ToastProvider';
import SignageMode from './SignageMode';
import { useAuth } from '../App';

const RANGES = ['1H', '6H', '24H', '7D', '30D'];

const METRIC_DEFINITIONS = [
  { key: 'battery', label: 'Battery', icon: <BatteryFullRounded sx={{ fontSize: 17 }} />, unit: '%', accent: palette.success, aliases: ['battery', 'battery_level', 'battery_pct', 'soc'] },
  { key: 'signal', label: 'Signal Strength', icon: <SignalCellularAltRounded sx={{ fontSize: 17 }} />, unit: 'dBm', accent: palette.primary, aliases: ['signal', 'signal_strength', 'rssi', 'wifi_rssi'] },
  { key: 'temperature', label: 'Temperature', icon: <ThermostatRounded sx={{ fontSize: 17 }} />, unit: '°C', accent: palette.warning, aliases: ['temperature', 'temp', 'temperature_c'] },
  { key: 'uptime', label: 'Device Uptime', icon: <TimerRounded sx={{ fontSize: 17 }} />, unit: 's', accent: palette.secondary, aliases: ['uptime', 'uptime_s', 'uptime_seconds', 'uptime_seconds_total'] },
  { key: 'gps', label: 'GPS Status', icon: <MyLocationRounded sx={{ fontSize: 17 }} />, unit: '', accent: palette.secondary, aliases: ['gps', 'latitude', 'longitude', 'gps_status'] },
  { key: 'network', label: 'Network Status', icon: <LanRounded sx={{ fontSize: 17 }} />, unit: '', accent: palette.primary, aliases: ['network', 'network_type', 'ip', 'protocol'] },
];

const pickValue = (data, aliases) => {
  if (!data) return undefined;
  const keys = Object.keys(data);
  for (const alias of aliases) {
    const match = keys.find((key) => key.toLowerCase() === alias);
    if (match !== undefined && data[match] !== null && data[match] !== undefined) return data[match];
  }
  return undefined;
};

const formatMetricValue = (definition, value) => {
  if (value === undefined || value === null) return '—';
  if (definition.key === 'gps') {
    const lat = pickValue(value, ['latitude', 'lat']);
    const lng = pickValue(value, ['longitude', 'lng', 'lon']);
    if (lat !== undefined && lng !== undefined) return `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`;
    return String(value);
  }
  if (definition.key === 'uptime') {
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return String(value);
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  return String(value);
};

const metricStatus = (definition, value) => {
  if (value === undefined || value === null || value === '') return { tone: 'neutral', label: 'No data' };
  if (definition.key === 'battery') {
    const numeric = Number(value);
    if (numeric < 20) return { tone: 'danger', label: 'Critical' };
    if (numeric < 45) return { tone: 'warning', label: 'Low' };
    return { tone: 'success', label: 'Healthy' };
  }
  if (definition.key === 'signal') {
    const numeric = Number(value);
    if (numeric < -90) return { tone: 'danger', label: 'Poor' };
    if (numeric < -70) return { tone: 'warning', label: 'Fair' };
    return { tone: 'success', label: 'Strong' };
  }
  if (definition.key === 'temperature') {
    const numeric = Number(value);
    if (numeric > 75) return { tone: 'danger', label: 'High' };
    if (numeric > 55) return { tone: 'warning', label: 'Elevated' };
    return { tone: 'success', label: 'Nominal' };
  }
  return { tone: 'success', label: 'Reported' };
};

export default function DeviceProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [device, setDevice] = useState(null);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState({ series: [], stats: {}, keys: [] });
  const [range, setRange] = useState('24H');
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [signageMode, setSignageMode] = useState(false);
  const [deviceOperations, setDeviceOperations] = useState([]);
  const [runningOperation, setRunningOperation] = useState(null);

  const loadDevice = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/devices/${id}`);
      setDevice(response.data);
      // Signage endpoints open directly in the dedicated signage visualisation.
      setSignageMode(Boolean(response.data.signage_required));
    } catch (error) {
      toast.error(errorMessage(error, 'The device profile could not be loaded.'));
      navigate('/devices');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toast]);

  const loadTelemetry = useCallback(
    async (activeRange) => {
      setChartLoading(true);
      try {
        const [historyResponse, latestResponse] = await Promise.allSettled([
          api.get(`/telemetry/device/${id}/history`, { params: { range: activeRange } }),
          api.get(`/telemetry/device/${id}/latest`),
        ]);

        setHistory(
          historyResponse.status === 'fulfilled' && historyResponse.value?.data
            ? historyResponse.value.data
            : { series: [], stats: {}, keys: [] }
        );

        if (latestResponse.status === 'fulfilled') {
          setLatest(latestResponse.value?.data);
        } else {
          setLatest(null);
        }
      } finally {
        setChartLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    loadDevice();
  }, [loadDevice]);

  useEffect(() => {
    loadTelemetry(range);
  }, [loadTelemetry, range]);

  const loadOperations = useCallback(async () => {
    try {
      const response = await api.get(`/devices/${id}/actions`);
      setDeviceOperations(response.data?.actions || []);
    } catch (error) {
      setDeviceOperations([]);
    }
  }, [id]);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  const latestData = useMemo(() => latest?.data || {}, [latest]);

  const metrics = useMemo(
    () =>
      METRIC_DEFINITIONS.map((definition) => {
        const value = pickValue(latestData, definition.aliases);
        return {
          ...definition,
          value,
          display: formatMetricValue(definition, value),
          status: metricStatus(definition, value),
        };
      }),
    [latestData]
  );

  const chartSeries = useMemo(() => {
    const series = history.series || [];
    const keys = history.keys || [];
    const preferred = ['temperature', 'humidity', 'battery', 'pressure', 'signal'];
    const selected = keys.filter((key) => preferred.includes(key.toLowerCase())).slice(0, 3);
    const fallback = selected.length ? selected : keys.slice(0, 3);
    return {
      labels: series.map((point) => {
        const timestamp = point.timestamp ? new Date(point.timestamp) : null;
        if (!timestamp || Number.isNaN(timestamp.getTime())) return '';
        return range === '24H' || range === '1H' || range === '6H'
          ? timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : timestamp.toLocaleDateString([], { day: '2-digit', month: 'short' });
      }),
      datasets: fallback.map((key, index) => ({
        label: key,
        data: series.map((point) => (point[key] === undefined ? null : point[key])),
        color: [palette.primary, palette.secondary, palette.success][index % 3],
      })),
    };
  }, [history, range]);

  const metricChart = useCallback(
    (key, color, title) => {
      const series = history.series || [];
      const labels = series.map((point) => {
        const timestamp = point.timestamp ? new Date(point.timestamp) : null;
        if (!timestamp || Number.isNaN(timestamp.getTime())) return '';
        return range === '1H' || range === '6H' || range === '24H'
          ? timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : timestamp.toLocaleDateString([], { day: '2-digit', month: 'short' });
      });
      const data = series.map((point) => (point[key] === undefined ? null : point[key]));
      const stats = history.stats?.[key];
      return (
        <GlassPanel sx={{ p: 3 }}>
          <SectionTitle
            title={title}
            subtitle={
              stats
                ? `Last ${stats.last ?? '—'} · min ${stats.min ?? '—'} · max ${stats.max ?? '—'} · avg ${
                    stats.avg !== undefined ? Number(stats.avg).toFixed(2) : '—'
                  }`
                : 'Awaiting telemetry'
            }
            icon={<SensorsRounded sx={{ fontSize: 18 }} />}
          />
          <Box sx={{ mt: 2 }}>
            {data.some((value) => value !== null) ? (
              <HoloLineChart labels={labels} datasets={[{ label: key, data, color }]} height={210} />
            ) : (
              <EmptyState title="No samples in this window" description="Widen the time range to see historical data." minHeight={180} />
            )}
          </Box>
        </GlassPanel>
      );
    },
    [history, range]
  );

  const latestRows = useMemo(() => {
    const rows = [];
    if (latest?.timestamp) {
      const data = latest.data || {};
      Object.entries(data).forEach(([key, value]) => {
        const definition = METRIC_DEFINITIONS.find((item) => item.aliases.includes(key.toLowerCase()));
        rows.push({
          id: key,
          timestamp: formatDateTime(latest.timestamp),
          parameter: key,
          value: typeof value === 'number' ? Number(value.toFixed(3)) : value,
          unit: definition?.unit || '',
          status: definition ? metricStatus(definition, value).label : 'Reported',
          tone: definition ? metricStatus(definition, value).tone : 'neutral',
        });
      });
    }
    return rows;
  }, [latest]);

  const runOperation = async (operation) => {
    setRunningOperation(operation.code);
    try {
      await api.post(`/devices/${id}/command`, {
        action: operation.code,
        label: operation.label,
      });
      toast.success(`"${operation.label}" dispatched to ${device.device_id}.`);
    } catch (error) {
      toast.error(errorMessage(error, 'The remote operation could not be dispatched.'));
    } finally {
      setRunningOperation(null);
    }
  };

  if (loading) {
    return (
      <GlassPanel>
        <PanelLoader label="Loading device profile" />
      </GlassPanel>
    );
  }

  if (!device) return null;

  // Signage endpoints switch to the dedicated signage visualisation only.
  if (device.signage_required && signageMode) {
    return <SignageMode device={device} onExit={() => setSignageMode(false)} />;
  }

  const hotspots = metrics
    .filter((metric) => metric.value !== undefined && metric.value !== null)
    .map((metric) => ({ id: metric.key, label: `${metric.label}: ${metric.display}` }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* breadcrumb + identity */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/devices')} sx={{ color: 'text.secondary' }}>
          <ArrowBackRounded sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', letterSpacing: '0.08em' }}>
          DEVICE PROFILE
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ borderColor: alpha(palette.primary, 0.18) }} />
        <Typography className="mono" sx={{ fontSize: '0.86rem', fontWeight: 600 }}>
          {device.device_id}
        </Typography>
        <StatusPill
          label={device.status}
          tone={device.status === 'online' ? 'success' : device.status === 'updating' ? 'warning' : 'danger'}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh telemetry">
          <IconButton onClick={() => loadTelemetry(range)} sx={{ color: 'text.secondary' }}>
            <RefreshRounded sx={{ fontSize: 18, animation: chartLoading ? 'holoSpin 900ms linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
        {deviceOperations.length > 0 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PowerSettingsNewRounded sx={{ fontSize: 15 }} />}
            onClick={() => runOperation(deviceOperations[0])}
          >
            {deviceOperations[0].label}
          </Button>
        )}
        {device.signage_required && (
          <Button
            size="small"
            variant="contained"
            startIcon={<ViewCarouselRounded sx={{ fontSize: 15 }} />}
            onClick={() => setSignageMode(true)}
            sx={{
              background: `linear-gradient(135deg, ${alpha(palette.secondary, 0.95)}, ${alpha(palette.primary, 0.85)})`,
              color: '#04070F',
            }}
          >
            Signage Mode
          </Button>
        )}
      </Box>

      {/* visual header */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <GlassPanel sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ position: 'relative' }}>
              <DeviceModel3D
                status={device.status}
                deviceType={device.device_type}
                label={device.device_id}
                hotspots={hotspots}
                onHotspot={(hotspot) => setActiveHotspot(hotspot)}
                height={360}
              />
              {activeHotspot && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: 16,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: 2,
                    background: alpha('#020617', 0.9),
                    border: `1px solid ${alpha(palette.primary, 0.35)}`,
                    animation: 'holoFloat 240ms ease',
                  }}
                >
                  <Typography sx={{ fontSize: '0.7rem', color: palette.primary, fontWeight: 600 }}>
                    {activeHotspot.label}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 1.5 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  bgcolor: device.status === 'online' ? palette.success : palette.danger,
                  boxShadow: `0 0 12px ${device.status === 'online' ? palette.success : palette.danger}`,
                  animation: 'holoPulse 2.4s ease-in-out infinite',
                }}
              />
              <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
                {device.status === 'online' ? 'Live connection established' : 'No active link'} · last seen{' '}
                {relativeTime(device.last_seen)}
              </Typography>
            </Box>
          </GlassPanel>
        </Grid>

        <Grid item xs={12} lg={7}>
          <GlassPanel sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.2 }}>
                  {(device.properties || {}).device_name || device.device_id}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                  <Typography className="mono" sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
                    {device.device_id}
                  </Typography>
                  {isAdmin && (
                    <Chip
                      size="small"
                      label={device.device_type}
                      sx={{ fontSize: '0.64rem', background: alpha(palette.primary, 0.12), color: palette.primary }}
                    />
                  )}
                  <Chip
                    size="small"
                    label={`fw ${device.firmware_version}`}
                    sx={{ fontSize: '0.64rem', background: alpha(palette.secondary, 0.12), color: palette.secondary }}
                  />
                  {device.owner_username && (
                    <Chip
                      size="small"
                      label={device.owner_username}
                      sx={{ fontSize: '0.64rem', background: alpha('#334155', 0.28), color: 'text.secondary' }}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.14em', color: 'text.secondary' }}>
                  LAST SEEN
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatDateTime(device.last_seen)}</Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 2.5 }} />

            <Box sx={{ display: 'grid', gap: 1.75, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' } }}>
              {metrics.map((metric) => (
                <Box
                  key={metric.key}
                  sx={{
                    p: 1.6,
                    borderRadius: 3,
                    border: `1px solid ${alpha(metric.accent, 0.16)}`,
                    background: `linear-gradient(150deg, ${alpha(metric.accent, 0.09)}, ${alpha('#070C1C', 0.45)})`,
                    transition: 'transform 260ms cubic-bezier(0.22,0.9,0.28,1), border-color 240ms ease',
                    '&:hover': { transform: 'translateY(-3px)', borderColor: alpha(metric.accent, 0.42) },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: metric.accent, mb: 0.9 }}>
                    {metric.icon}
                    <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.12em', fontWeight: 700, color: 'text.secondary' }}>
                      {metric.label.toUpperCase()}
                    </Typography>
                  </Box>
                  <Typography className="mono" sx={{ fontSize: '1.05rem', fontWeight: 500, lineHeight: 1.2 }}>
                    {metric.display}
                    {metric.unit && metric.key !== 'uptime' && metric.key !== 'gps' && (
                      <Box component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary', ml: 0.4 }}>
                        {metric.unit}
                      </Box>
                    )}
                  </Typography>
                  <Box sx={{ mt: 0.9 }}>
                    <StatusPill label={metric.status.label} tone={metric.status.tone} pulse={false} />
                  </Box>
                </Box>
              ))}
            </Box>
          </GlassPanel>
        </Grid>
      </Grid>

      {/* operation console */}
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Operation Console"
          subtitle="Remote operations enabled for this endpoint by an administrator"
          icon={<BoltRounded sx={{ fontSize: 18 }} />}
          action={
            <StatusPill
              label={device.status === 'online' ? 'endpoint linked' : 'endpoint offline'}
              tone={device.status === 'online' ? 'success' : 'danger'}
            />
          }
        />

        <Typography
          sx={{
            mt: 2.5,
            mb: 1.5,
            fontSize: '0.64rem',
            fontWeight: 700,
            letterSpacing: '0.16em',
            color: 'text.secondary',
          }}
        >
          REMOTE ACTIONS
        </Typography>

        {deviceOperations.length === 0 ? (
          <EmptyState
            icon={<BoltRounded />}
            title="No remote actions available"
            description="An administrator has not enabled any remote operation for this endpoint."
            minHeight={140}
          />
        ) : (
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
            {deviceOperations.map((operation) => (
              <Box
                key={operation.code}
                sx={{
                  position: 'relative',
                  p: 1.6,
                  borderRadius: 3,
                  border: `1px solid ${alpha(palette.secondary, 0.24)}`,
                  background: `linear-gradient(150deg, ${alpha(palette.secondary, 0.12)}, ${alpha('#070C1C', 0.5)})`,
                  transition: 'transform 240ms cubic-bezier(0.22,0.9,0.28,1), border-color 240ms ease',
                  '&:hover': { transform: 'translateY(-3px)', borderColor: alpha(palette.secondary, 0.55) },
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<BoltRounded sx={{ fontSize: 15 }} />}
                  onClick={() => runOperation(operation)}
                  disabled={runningOperation === operation.code}
                  sx={{
                    background: `linear-gradient(135deg, ${alpha(palette.secondary, 0.92)}, ${alpha(palette.primary, 0.85)})`,
                    color: '#04070F',
                  }}
                >
                  {runningOperation === operation.code ? 'Dispatching…' : operation.label}
                </Button>
                <Typography className="mono" sx={{ fontSize: '0.62rem', color: 'text.secondary', mt: 0.85, textAlign: 'center' }}>
                  {operation.code}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </GlassPanel>

      {/* charts */}
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Sensor Data"
          subtitle="Historical telemetry for the selected window"
          icon={<SensorsRounded sx={{ fontSize: 18 }} />}
          action={
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {RANGES.map((option) => (
                <Button
                  key={option}
                  size="small"
                  onClick={() => setRange(option)}
                  variant={range === option ? 'contained' : 'outlined'}
                  sx={{ minWidth: 52, py: 0.55, fontSize: '0.72rem' }}
                >
                  {option}
                </Button>
              ))}
            </Box>
          }
        />
        <Box sx={{ mt: 2.5 }}>
          {chartLoading && !history.series?.length ? (
            <Box sx={{ height: 260, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={24} thickness={3} />
            </Box>
          ) : chartSeries.datasets.length ? (
            <HoloLineChart labels={chartSeries.labels} datasets={chartSeries.datasets} height={280} legend />
          ) : (
            <EmptyState
              icon={<SensorsRounded />}
              title="No telemetry in this window"
              description="The endpoint has not reported data for the selected period."
              minHeight={240}
            />
          )}
        </Box>
      </GlassPanel>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          {metricChart('battery', palette.success, 'Battery Level')}
        </Grid>
        <Grid item xs={12} md={6}>
          {metricChart('temperature', palette.warning, 'Temperature')}
        </Grid>
      </Grid>

      {/* latest data */}
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Latest Data"
          subtitle="Most recent reading reported by the endpoint"
          icon={<SensorsRounded sx={{ fontSize: 18 }} />}
        />
        <Box sx={{ mt: 2, overflowX: 'auto' }}>
          {latestRows.length === 0 ? (
            <EmptyState
              icon={<SensorsRounded />}
              title="No readings available"
              description="The endpoint has not transmitted telemetry yet."
              minHeight={180}
            />
          ) : (
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <Box component="thead">
                <Box component="tr">
                  {['Timestamp', 'Parameter', 'Value', 'Unit', 'Status'].map((header) => (
                    <Box
                      component="th"
                      key={header}
                      sx={{
                        textAlign: 'left',
                        py: 1.25,
                        px: 1.5,
                        fontSize: '0.66rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        borderBottom: `1px solid ${alpha(palette.primary, 0.12)}`,
                        fontWeight: 700,
                      }}
                    >
                      {header}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {latestRows.map((row) => (
                  <Box
                    component="tr"
                    key={row.id}
                    sx={{ '&:hover': { background: alpha(palette.primary, 0.045) }, transition: 'background-color 180ms ease' }}
                  >
                    <Box component="td" sx={{ py: 1.4, px: 1.5, fontSize: '0.76rem', color: 'text.secondary' }}>
                      {row.timestamp}
                    </Box>
                    <Box component="td" sx={{ py: 1.4, px: 1.5 }}>
                      <Typography className="mono" sx={{ fontSize: '0.76rem' }}>
                        {row.parameter}
                      </Typography>
                    </Box>
                    <Box component="td" sx={{ py: 1.4, px: 1.5 }}>
                      <Typography className="mono" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                        {row.value}
                      </Typography>
                    </Box>
                    <Box component="td" sx={{ py: 1.4, px: 1.5 }}>
                      <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>{row.unit || '—'}</Typography>
                    </Box>
                    <Box component="td" sx={{ py: 1.4, px: 1.5 }}>
                      <StatusPill label={row.status} tone={row.tone} pulse={false} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </GlassPanel>
    </Box>
  );
}
